import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Icon,
  Select,
  useDisclosure,
  SimpleGrid,
  Divider,
  useToast,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const User_Reg_Form = () => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [trainer, setTrainer] = useState("");
  const schoolId = sessionStorage.getItem("school_id");
  const studentId = sessionStorage.getItem("student_id");
  const userId = sessionStorage.getItem("user_id");

  // Fetch event titles
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/event/list_all_event.php?school_id=${schoolId}`
        );
        if (response.data.success) {
          setEvents(response.data.data.events || []);
        } else {
          toast({
            title: "Error Fetching Events",
            description: "An error occurred while fetching events.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        toast({
          title: "Error Fetching Events",
          description: "An error occurred while fetching events.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchEvents();
  }, [schoolId]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/events/category/list_category.php?school_id=${schoolId}`
        );
        if (response.data.success) {
          setCategories(response.data.data || []);
        } else {
          toast({
            title: "Error Fetching Categories",
            description: "An error occurred while fetching categories.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast({
          title: "Error Fetching Categories",
          description: "An error occurred while fetching categories.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchCategories();
  }, [schoolId]);

  // Fetch subcategories based on selected category
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (category) {
        try {
          const response = await axios.get(
            `${BASE_URL}/admin/events/sub_category/list_subcat.php?school_id=${schoolId}`
          );
          if (response.data.success) {
            const subcategoriesForCategory = [];
            response.data.data.forEach((eventData) => {
              eventData.categories.forEach((categoryData) => {
                if (categoryData.category_id === parseInt(category, 10)) {
                  subcategoriesForCategory.push(...categoryData.sub_categories);
                }
              });
            });
            setFilteredSubcategories(subcategoriesForCategory);
          } else {
            toast({
              title: "Error Fetching Subcategories",
              description: "An error occurred while fetching subcategories.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error fetching subcategories:", error);
          toast({
            title: "Error Fetching Subcategories",
            description: "An error occurred while fetching subcategories.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    };
    fetchSubcategories();
  }, [category, schoolId]);

  // Filter categories when selectedEvent changes
  useEffect(() => {
    if (selectedEvent) {
      const filtered = categories.filter(
        (cat) => cat.event_id === parseInt(selectedEvent, 10)
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories([]);
    }
    setCategory("");
    setSubcategory("");
    setTrainer("");
  }, [selectedEvent, categories]);

  const handleEventChange = (e) => {
    setSelectedEvent(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSubcategory("");
    setTrainer("");
  };

  const handleSubcategoryChange = (e) => {
    setSubcategory(e.target.value);
    const selectedSubcat = filteredSubcategories.find(
      (subcat) => subcat.subcat_id === parseInt(e.target.value, 10)
    );
    setTrainer(selectedSubcat?.allocated_teacher || "");
  };

  const handleSubmit = async () => {
    const registrationData = {
      user_id: userId,
      school_id: schoolId,
      event_id: selectedEvent,
      category_id: category,
      subcategory_id: subcategory,
      student_id: studentId,
    };
    console.log("regdata", registrationData);

    try {
      const response = await axios.post(
        `${BASE_URL}/student/events/create_registration.php`,
        registrationData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("creat_reg_studnt", response);

      if (response.data.success) {
        toast({
          title: "Registration Successful",
          description: "Your registration was successful.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose(); // Close the modal after successful registration
      } else {
        toast({
          title: "Error",
          description: "Error: " + response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      toast({
        title: "Error Submitting Registration",
        description: "An error occurred while submitting the registration.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button
        leftIcon={<FaPlus />}
        colorScheme="purple"
        onClick={onOpen}
        display={{ base: "flex", md: "inline-flex" }} // Show as flex on mobile and inline-flex on larger devices
      >
        {useBreakpointValue({ base: "", md: "Event Registration" })}{" "}
        {/* Display only icon on mobile and full text on larger screens */}
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            {" "}
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Student Registration
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl id="eventTitle">
                <FormLabel>Event Title</FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Event"
                  value={selectedEvent}
                  onChange={handleEventChange}
                >
                  {events && events.length > 0 ? (
                    events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.event_title}
                      </option>
                    ))
                  ) : (
                    <option disabled>No events available</option>
                  )}
                </Select>
              </FormControl>

              <FormControl id="category">
                <FormLabel>Category</FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Category"
                  value={category}
                  onChange={handleCategoryChange}
                  isDisabled={!selectedEvent}
                >
                  {filteredCategories && filteredCategories.length > 0 ? (
                    filteredCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category}
                      </option>
                    ))
                  ) : (
                    <option disabled>No categories available</option>
                  )}
                </Select>
              </FormControl>

              <FormControl id="subcategory">
                <FormLabel>Subcategory</FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  placeholder="Select Subcategory"
                  value={subcategory}
                  onChange={handleSubcategoryChange}
                  isDisabled={!category}
                >
                  {filteredSubcategories && filteredSubcategories.length > 0 ? (
                    filteredSubcategories.map((subcat) => (
                      <option key={subcat.subcat_id} value={subcat.subcat_id}>
                        {subcat.sub_category}
                      </option>
                    ))
                  ) : (
                    <option disabled>No subcategories available</option>
                  )}
                </Select>
              </FormControl>

              <FormControl id="trainer">
                <FormLabel>Allocated Trainer</FormLabel>
                <Input type="text" value={trainer} isReadOnly />
              </FormControl>
            </SimpleGrid>

            <Divider mt={5} />
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleSubmit}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default User_Reg_Form;
